import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { T } from "@repo/transifex";
import { OrdersList } from "~/routes/__app.collect.orders/basket/orders-list";
import { Button } from "~/components";
import { Tabs } from "~/components/tabs";
import { type TransformedCollectOrder } from "~/api/collect/hooks/use-get-collect-order";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { timeFormatter } from "~/utils/time-formatter";
import {
  useCollectOrderLines,
  useCollectSuccessHandler
} from "~/api/collect/hooks/use-collect-order-lines";
import { collectActions } from "~/providers/store/collect/actions";
import {
  useCollectFlow,
  useOrderLinesToCollect
} from "~/providers/store/collect/hooks";
import { parseOrderLinesToCollect } from "~/utils/collect/parse-order-lines-to-collect";
import { useCollectTillContext } from "~/routes/__app.collect/collect-till-context";

import { CollectBasketLayout } from "./layout";
import { ActiveOrderList } from "./active-order-list";
import { CollectButton } from "./collect-button";

export type BasketFilter = "active" | "collected";

type BasketProps = {
  order: TransformedCollectOrder;
  childId: string;
};

export const Basket = ({ order, childId }: BasketProps) => {
  const [activeTab, setActiveTab] = useState<BasketFilter>(() => {
    return order.activeOrderLines.length > 0 ? "active" : "collected";
  });

  const navigate = useNavigate();
  const { language } = useCurrentTillLocalisation();
  const till = useCollectTillContext((till) => till);
  const { mutate: collectOrderLines, isPending: isCollecting } =
    useCollectOrderLines();
  const handleCollectSuccess = useCollectSuccessHandler();

  const collectFlow = useCollectFlow();
  const orderLinesToCollect = useOrderLinesToCollect();

  useEffect(() => {
    collectActions.init(order.activeOrderLines.map((line) => line.id));
  }, [order]);

  const handleSubmit = () => {
    const orderLines = parseOrderLinesToCollect({
      orderLineToCollectIds: orderLinesToCollect,
      activeOrderLines: order.activeOrderLines
    });

    collectOrderLines(
      {
        childId,
        orderLines
      },
      {
        onSuccess: () => {
          handleCollectSuccess({
            collectedOrderLines: orderLines.length,
            isFullyCollected:
              order.activeOrderLines.length === orderLinesToCollect.length,
            forceRedirect: true
          });
        }
      }
    );
  };

  return (
    <CollectBasketLayout
      tabs={
        <Tabs>
          <Tabs.Item
            isActive={activeTab === "active"}
            disabled={order.activeOrderLines.length === 0}
            onClick={() => {
              setActiveTab("active");
            }}
          >
            <T _str="Active basket" />
            <span className="!font-normal text-interface-neutral-4">
              {order.activeOrderLines.length}
            </span>
          </Tabs.Item>

          <Tabs.Item
            isActive={activeTab === "collected"}
            disabled={order.collectedOrderLines.length === 0}
            onClick={() => {
              setActiveTab("collected");
            }}
          >
            <T _str="Collected" />
            <span className="!font-normal text-interface-neutral-4">
              {order.collectedOrderLines.length}
            </span>
          </Tabs.Item>
        </Tabs>
      }
      footer={
        till.type === "collect" ? (
          <>
            {till.config.showAddItemsButton ? (
              <Button
                size="xl"
                variant="secondary"
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => {
                  // if the user comes from POS, don't overwrite the flow
                  collectActions.setFlow(
                    collectFlow === "collect" ? "collect-pos" : "pos"
                  );
                  navigate(`/pos/sell`);
                }}
              >
                <T _str="Add items" />
              </Button>
            ) : null}

            <CollectButton
              disabled={
                order.activeOrderLines.length === 0 ||
                orderLinesToCollect.length === 0
              }
              onSubmit={handleSubmit}
              isSubmitting={isCollecting}
            />
          </>
        ) : null
      }
    >
      {activeTab === "active" ? (
        <ActiveOrderList
          order={order}
          isSelectable={till.type === "collect"}
          childId={childId}
        />
      ) : (
        <OrdersList variant="menu">
          {order.collectedOrderLines.map((orderLine) => (
            <OrdersList.CollectedItem
              key={orderLine.id}
              name={orderLine.productName}
              collectedAt={timeFormatter({
                timestamp: orderLine.collectedAt,
                language,
                timezone: till.timezone
              })}
              options={orderLine.options}
              productAllergens={orderLine.allergens}
            />
          ))}
        </OrdersList>
      )}
    </CollectBasketLayout>
  );
};
